// Harshada Bhavsar (23/07/2025)
import React, { useState, useRef } from "react";
import {
  VerifyCandidate,
  SendCandidate,
} from "../CServices/CSendCandidateService";
import { useNavigate, useParams } from "react-router-dom";
import Cookies from "js-cookie";
import NormalAlert from "../../AlertComponent/NormalAlert";
import ConfirmationAlert from "../../AlertComponent/ConfirmationAlert";

const Modal = ({ show, onClose, children, title }) => {
  if (!show) return null;
  return (
    <>
      <div
        className="modal fade show"
        style={{ display: "block" }}
        tabIndex="-1"
      >
        <div className="modal-dialog modal-xl modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">{title}</h5>
              <button
                type="button"
                className="btn-close"
                onClick={onClose}
              ></button>
            </div>
            <div className="modal-body">{children}</div>
          </div>
        </div>
      </div>
      <div
        className="modal-backdrop fade show"
        onClick={onClose}
        style={{ zIndex: 1040 }}
      ></div>
    </>
  );
};

const CSendCandidate = () => {
  const navigate = useNavigate();
  const token = Cookies.get("ConsultantToken");
  const { jobUID } = useParams();
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    CandidateName: "",
    CandidateEmail: "",
    CandidatePhoneNo: "",
    CandidatePAN: "",
    CandidateDOB: "",
    JobUID: "",
    Token: "",
  });

  const initialFormData = {
    CandidateName: "",
    CandidateEmail: "",
    CandidatePhoneNo: "",
    CandidatePAN: "",
    CandidateDOB: "",
  };

  const [sendFormData, setSendFormData] = useState({
    CurrentCTC: "",
    ExpectedCTC: "",
    TotalExperience: "",
    CurrentLocation: "",
    NoticePeriod: "",
    ReadytoRelocate: "",
    ReasonofLeaving: "",
    Remarks: "",
    ResumeFile: null,
  });

  const [requestId, setRequestId] = useState("");
  const [showSendForm, setShowSendForm] = useState(false);

  const [message, setMessage] = useState("");
  const [errors, setErrors] = useState({});
  const [error, setError] = useState("");
  const [alert, setAlert] = useState(null);
  const [confirmalert, confirmsetAlert] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setMessage("");
    setError("");
  };

  const handleSendChange = (e) => {
    const { name, value, type, checked, files } = e.target;

    if (type === "file") {
      const file = files[0];
      if (file) {
        const allowedTypes = [
          "application/pdf",
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        ];
        const maxSizeMB = 5;
        const maxSizeBytes = maxSizeMB * 1024 * 1024;

        if (!allowedTypes.includes(file.type)) {
          setAlert({
            type: "fail",
            message: "Only PDF or DOCX files are allowed.",
          });
          return;
        }
        if (file.size > maxSizeBytes) {
          setAlert({
            type: "fail",
            message: `File size must not exceed ${maxSizeMB} MB.`,
          });
          return;
        }
        setSendFormData({ ...sendFormData, [name]: file });
      }
    } else if (type === "checkbox") {
      setSendFormData({ ...sendFormData, [name]: checked });
    } else {
      setSendFormData({ ...sendFormData, [name]: value });
    }
  };

  const handleSendSubmit = async (e) => {
    e.preventDefault();

    if (!sendFormData.ResumeFile) {
      setAlert({ type: "fail", message: "Resume file is required." });
      return;
    }

    try {
      const payload = {
        Token: token,
        RequestID: requestId,
        ...sendFormData,
        ReadytoRelocate: sendFormData.ReadytoRelocate ? "Yes" : "No", // convert to string
      };

      const res = await SendCandidate(payload);

      if (res.data?.status === "Success") {
        setAlert({
          type: "success",
          message: res.data?.message || "Candidate sent.",
        });
        setShowSendForm(false);
        setSendFormData({
          CurrentCTC: "",
          ExpectedCTC: "",
          TotalExperience: "",
          CurrentLocation: "",
          NoticePeriod: "",
          ReadytoRelocate: "",
          ReasonofLeaving: "",
          Remarks: "",
          ResumeFile: null,
        });
        if (fileInputRef.current) fileInputRef.current.value = "";
      } else {
        setAlert({
          type: "fail",
          message: res.data?.message || "Unable to send candidate",
        });
      }
    } catch (err) {
      setAlert({
        type: "fail",
        message: err.response?.data?.message || "Error sending candidate",
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.CandidateName)
      newErrors.CandidateName = "Candidate Name is required";
    if (!formData.CandidatePhoneNo)
      newErrors.CandidatePhoneNo = "Candidate Phone No is required";

    if (formData.CandidateDOB) {
      const dob = new Date(formData.CandidateDOB);
      const age = new Date().getFullYear() - dob.getFullYear();
      const m = new Date().getMonth() - dob.getMonth();
      if (age < 18 || (age === 18 && m < 0)) {
        newErrors.CandidateDOB = "Candidate must be 18 years or older.";
      }
    }
    const today = new Date();
    const selectedDOB = new Date(formData.CandidateDOB);
    if (selectedDOB > today) {
      newErrors.CandidateDOB = "Date of Birth cannot be in the future.";
    }
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const clientErrors = validateForm();
    if (Object.keys(clientErrors).length > 0) {
      setErrors(clientErrors);
      return;
    }
    try {
      const payload = {
        ...formData,
        JobUID: jobUID,
        Token: token,
      };
      const response = await VerifyCandidate(payload);
      const data = response.data;

      if (data.status === "Success") {
        setRequestId(data.RequestID || data.requestId || data.requestID || "");

        setAlert({ type: data.status, message: data.message });
        setShowSendForm(true); // now opens modal
        setFormData(initialFormData);
      } else {
        setError(data.message || "Something went wrong.");
      }
    } catch (error) {
      const safeMessage =
        error?.response?.data?.message ||
        "Something went wrong. Please try again.";

      setAlert({
        type: "fail",
        message: safeMessage,
      });
      setShowConfirm(true);
      confirmsetAlert({ type: "fail", message: error?.response?.data?.message });
    }
  };

  const handleReset = () => {
    setFormData(initialFormData);
    setErrors({});
  };

  const handleConfirm = () => {
    setShowConfirm(false);
    setFormData(initialFormData);
    setShowSendForm(true);
    setErrors({});
    setMessage("");
    setError("");
  };

  const handleCancel = () => {
    setShowConfirm(false);
  };

  return (
    <>
      <div className="SetuSbg"></div>
      <div className="form-over-image w-100">
        <div
          className="container p-5 my-5 shadow bg-white rounded role-form-container"
          style={{ maxWidth: "900px" }}
        >
          <div className="">
            <div className="form-header">
              <i className="bi bi-person-fill avatar-icon"></i>
              <h2 className="form-title">Submit Candidate</h2>
            </div>
            <hr className="text-dark" />
            <form onSubmit={handleSubmit} className="row">
              <div className="form-group col-md-6 col-12 mb-3">
                <label className="form-label text-black">
                  Candidate Name<span style={{ color: "red" }}> *</span>
                </label>
                <input
                  type="text"
                  name="CandidateName"
                  placeholder="Candidate Name"
                  value={formData.CandidateName}
                  onChange={handleChange}
                  className={`input100 form-control ${
                    errors.CandidateName ? "is-invalid" : ""
                  }`}
                />
                {errors.CandidateName && (
                  <div className="invalid-feedback">{errors.CandidateName}</div>
                )}
              </div>
              <div className="form-group col-md-6 col-12 mb-3">
                <label className="form-label text-black">Candidate Email</label>
                <input
                  type="text"
                  name="CandidateEmail"
                  placeholder="Candidate Email"
                  value={formData.CandidateEmail}
                  onChange={handleChange}
                  className="input100 form-control"
                />
              </div>
              <div className="form-group col-md-6 col-12 mb-3">
                <label className="form-label text-black">
                  Candidate Phone No<span style={{ color: "red" }}> *</span>
                </label>
                <input
                  type="tel"
                  id="phoneNumber"
                  name="CandidatePhoneNo"
                  placeholder="Candidate Phone No"
                  value={formData.CandidatePhoneNo}
                  maxLength="10"
                  pattern="\d{10}"
                  onChange={(e) => {
                    const val = e.target.value;
                    if (/^\d*$/.test(val)) {
                      handleChange(e);
                    }
                  }}
                  className={`input100 form-control ${
                    errors.CandidatePhoneNo ? "is-invalid" : ""
                  }`}
                />
                {errors.CandidatePhoneNo && (
                  <div className="invalid-feedback">
                    {errors.CandidatePhoneNo}
                  </div>
                )}
              </div>
              <div className="form-group col-md-6 col-12 mb-3">
                <label className="form-label text-black">Candidate PAN</label>
                <input
                  type="text"
                  name="CandidatePAN"
                  placeholder="Candidate PAN"
                  value={formData.CandidatePAN}
                  onChange={handleChange}
                  className="input100 form-control"
                />
              </div>
              <div className="form-group col-md-6 col-12 mb-3">
                <label className="form-label text-black">Candidate DOB</label>
                <input
                  type="date"
                  name="CandidateDOB"
                  placeholder="Candidate DOB"
                  value={formData.CandidateDOB}
                  className="input100 form-control"
                  onChange={handleChange}
                  max={new Date().toISOString().split("T")[0]}
                />
              </div>
              <div className="d-flex justify-content-center form-actions">
                <button
                  type="submit"
                  className="btn btn-primary px-5 py-2 mt-4 me-3"
                >
                  Verify
                </button>
                <button
                  type="reset"
                  className="btn btn-light px-5 py-2 mt-4 me-3"
                  onClick={handleReset}
                >
                  Reset
                </button>
              </div>
            </form>

            {message && <div className="success-message">{message}</div>}
            {error && <div className="error-message">{error}</div>}
          </div>
        </div>
      </div>

      {/* Modal for Send Candidate Form */}
      <Modal
        show={showSendForm}
        onClose={() => setShowSendForm(false)}
        title="Send Candidate Details"
      >
        <form onSubmit={handleSendSubmit} className="row mt-3">
          <div className="form-group col-md-6 mb-3">
            <label>
              Current CTC <span style={{ color: "red" }}> *</span>
            </label>
            <input
              type="number"
              min={0}
              name="CurrentCTC"
              placeholder="Current CTC"
              value={formData.CurrentCTC}
              onChange={handleSendChange}
              className={`input100 form-control ${
                errors.CurrentCTC ? "is-invalid" : ""
              }`}
            />
            {errors.CurrentCTC && (
              <div className="invalid-feedback">{errors.CurrentCTC}</div>
            )}
          </div>
          <div className="form-group col-md-6 mb-3">
            <label>
              Expected CTC <span style={{ color: "red" }}> *</span>
            </label>
            <input
              type="number"
              min={0}
              name="ExpectedCTC"
              placeholder="Expected CTC"
              value={formData.ExpectedCTC}
              onChange={handleSendChange}
              className={`input100 form-control ${
                errors.ExpectedCTC ? "is-invalid" : ""
              }`}
            />
            {errors.ExpectedCTC && (
              <div className="invalid-feedback">{errors.ExpectedCTC}</div>
            )}
          </div>
          <div className="form-group col-md-6 mb-3">
            <label>
              Total Experience <span style={{ color: "red" }}> *</span>
            </label>
            <input
              type="number"
              min={0}
              name="TotalExperience"
              placeholder="Total Experience"
              value={formData.TotalExperience}
              onChange={handleSendChange}
              className={`input100 form-control ${
                errors.TotalExperience ? "is-invalid" : ""
              }`}
            />
            {errors.TotalExperience && (
              <div className="invalid-feedback">{errors.TotalExperience}</div>
            )}
          </div>
          <div className="form-group col-md-6 mb-3">
            <label>
              Current Location <span style={{ color: "red" }}> *</span>
            </label>
            <input
              type="text"
              name="CurrentLocation"
              placeholder="Current Location"
              value={formData.CurrentLocation}
              onChange={handleSendChange}
              className={`input100 form-control ${
                errors.CurrentLocation ? "is-invalid" : ""
              }`}
            />
            {errors.CurrentLocation && (
              <div className="invalid-feedback">{errors.CurrentLocation}</div>
            )}
          </div>
          <div className="form-group col-md-6 mb-3">
            <label>
              Notice Period (in days) <span style={{ color: "red" }}> *</span>
            </label>
            <input
              type="number"
              min={0}
              name="NoticePeriod"
              placeholder="Notice Period"
              value={formData.NoticePeriod}
              onChange={handleSendChange}
              className={`input100 form-control ${
                errors.NoticePeriod ? "is-invalid" : ""
              }`}
            />
            {errors.NoticePeriod && (
              <div className="invalid-feedback">{errors.NoticePeriod}</div>
            )}
          </div>
          <div className="form-group col-md-6 mb-3">
            <label>
              Ready to Relocate <span style={{ color: "red" }}> *</span>
            </label>
            <select
              type="text"
              name="ReadytoRelocate"
              placeholder="Ready to Relocate"
              value={formData.ReadytoRelocate}
              onChange={handleSendChange}
              className={`input100 form-control ${
                errors.ReadytoRelocate ? "is-invalid" : ""
              }`}
            >
              <option>Yes</option>
              <option>No</option>
            </select>

            <i
              // className="bi bi-chevron-down position-absolute"
              className={`ms-3 position-absolute bi bi-chevron-down`}
              style={{
                right: "20px",
                top: "45%",
                transform: "translateY(-45%)",
                pointerEvents: "none",
                color: "#555",
              }}
            ></i>
            {errors.ReadytoRelocate && (
              <div className="invalid-feedback">{errors.ReadytoRelocate}</div>
            )}
          </div>
          <div className="form-group col-md-6 mb-3">
            <label>
              Reason of Leaving <span style={{ color: "red" }}> *</span>
            </label>
            <input
              type="text"
              name="ReasonofLeaving"
              placeholder="Reason of Leaving"
              value={formData.ReasonofLeaving}
              onChange={handleSendChange}
              className={`input100 form-control ${
                errors.ReasonofLeaving ? "is-invalid" : ""
              }`}
            />
            {errors.ReasonofLeaving && (
              <div className="invalid-feedback">{errors.ReasonofLeaving}</div>
            )}
          </div>
          <div className="form-group col-md-6 mb-3">
            <label>
              Remarks <span style={{ color: "red" }}> *</span>
            </label>
            <input
              type="text"
              name="Remarks"
              placeholder="Remarks"
              value={formData.Remarks}
              onChange={handleSendChange}
              className={`input100 form-control ${
                errors.Remarks ? "is-invalid" : ""
              }`}
            />
            {errors.Remarks && (
              <div className="invalid-feedback">{errors.Remarks}</div>
            )}
          </div>
          <div className="form-group col-md-6 mb-3">
            <label>
              Resume <span style={{ color: "red" }}> *</span>
            </label>
            <input
              type="file"
              name="ResumeFile"
              placeholder="Resume File"
              value={formData.ResumeFile}
              onChange={handleSendChange}
              className={`input100 form-control ${
                errors.ResumeFile ? "is-invalid" : ""
              }`}
            />
            {errors.ResumeFile && (
              <div className="invalid-feedback">{errors.ResumeFile}</div>
            )}
          </div>
          <div className="d-flex justify-content-center">
            <button type="submit" className="btn btn-success px-5 py-2 mt-4">
              Send Candidate
            </button>
          </div>
        </form>
      </Modal>

      {alert && (
        <NormalAlert
          type={alert.type}
          message={alert.message}
          onClose={() => setAlert(null)}
        />
      )}
      {confirmalert && (
        <ConfirmationAlert
          title={confirmalert.type}
          message={confirmalert.message}
          onClose={() => setAlert(null)}
          onConfirm={handleConfirm}
          onCancel={handleCancel}
        />
      )}
    </>
  );
};

export default CSendCandidate;
